import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackendContext } from '../Context/BackendContext';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Link,
  Avatar,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Select,
  SelectItem,
  Button,
  Skeleton,
  Tooltip,
  Input,
} from "@heroui/react";
import {
  Blocks,
  MessageCircleQuestion,
  Package,
  Store,
  Crown,
  LogOut,
  Headset,
  RefreshCw,
  SquarePlus,
  Search,
} from 'lucide-react';
import artParcelLogo from '../../public/artParcelLogo.png';
import NavbarNewsItem from './NewsModal';
import AdminModal from './AdminModal';
import { ChromaIcon } from './/Icons/ChromaIcon.tsx';
import { getPlayerProgress } from '../helper/playerProgress';

const formatInt = (n) => Number(n ?? 0).toLocaleString('en-US');
const BLOCKED_SEARCH_TERMS = [
  'asshole',
  'bastard',
  'bitch',
  'bullshit',
  'cock',
  'cunt',
  'dick',
  'douche',
  'fag',
  'faggot',
  'fuck',
  'motherfucker',
  'nigger',
  'penis',
  'pussy',
  'shit',
  'slut',
  'twat',
  'whore',
];

const normalizeSearchText = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const containsBlockedSearchTerm = (value = '') => {
  const normalized = normalizeSearchText(value);
  if (!normalized) return false;

  const compact = normalized.replace(/\s+/g, '');
  return BLOCKED_SEARCH_TERMS.some((term) => {
    const normalizedTerm = normalizeSearchText(term).replace(/\s+/g, '');
    return normalized.includes(term) || compact.includes(normalizedTerm);
  });
};

const getLevelAccent = (level) => {
  if (level >= 50) {
    return {
      ring: '#f59e0b',
      ringSecondary: '#fde68a',
      glow: 'rgba(245, 158, 11, 0.28)',
      badge: 'border-amber-300/50 bg-amber-400/10 text-amber-200',
      label: 'text-amber-200',
      pulse: true,
    };
  }

  if (level >= 35) {
    return {
      ring: '#e879f9',
      ringSecondary: '#a78bfa',
      glow: 'rgba(232, 121, 249, 0.28)',
      badge: 'border-fuchsia-300/50 bg-fuchsia-400/10 text-fuchsia-200',
      label: 'text-fuchsia-200',
      pulse: true,
    };
  }

  if (level >= 20) {
    return {
      ring: '#22d3ee',
      ringSecondary: '#60a5fa',
      glow: 'rgba(34, 211, 238, 0.26)',
      badge: 'border-cyan-300/50 bg-cyan-400/10 text-cyan-200',
      label: 'text-cyan-200',
      pulse: false,
    };
  }

  if (level >= 10) {
    return {
      ring: '#34d399',
      ringSecondary: null,
      glow: 'rgba(52, 211, 153, 0.24)',
      badge: 'border-emerald-300/50 bg-emerald-400/10 text-emerald-200',
      label: 'text-emerald-200',
      pulse: false,
    };
  }

  return {
    ring: '#a78bfa',
    ringSecondary: null,
    glow: 'rgba(167, 139, 250, 0.22)',
    badge: 'border-indigo-400/40 bg-indigo-500/10 text-indigo-300',
    label: 'text-indigo-300',
    pulse: false,
  };
};

export default function AppNavbar() {
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [canvasOptions, setCanvasOptions] = useState([]);

  const { signOut } = useAuthenticator();
  const { userProfile, isUserLoading, isAdmin, listCanvases } = useContext(BackendContext);
  const navigate = useNavigate();
  const location = useLocation();
  const currentCategory = location.pathname.split('/')[2];
  const searchWrapRef = useRef(null);

  const currentSection = location.pathname.split('/')[1] || '';
  const currentCanvasPage = location.pathname.split('/')[2] || '';
  const resolvedArtworkHref = `/Canvas/${encodeURIComponent(currentCanvasPage || 'A')}`;
  const searchBasePath = currentSection === 'Canvas' ? '/Canvas' : '/PlaceCanvas';
  const searchSubtitle = currentSection === 'Canvas' ? 'Artwork Page' : 'Free Draw Page';
  const searchPlaceholder = currentSection === 'Canvas' ? 'Search artwork pages' : 'Search free draw pages';

  useEffect(() => {
    let alive = true;

    async function loadCanvasOptions() {
      const canvases = await listCanvases?.();
      if (!alive) return;

      const freeDrawPages = (canvases ?? []).map((entry) => ({
        id: `free-draw-${entry.page}`,
        label: entry.page,
        subtitle: searchSubtitle,
        keywords: `${entry.page}`,
        href: `${searchBasePath}/${encodeURIComponent(entry.page)}`,
      })).filter((entry) => !containsBlockedSearchTerm(entry.label));

      setCanvasOptions(freeDrawPages);

    }

    loadCanvasOptions();
    return () => {
      alive = false;
    };
  }, [listCanvases, location.pathname, searchFocused, searchBasePath, searchSubtitle]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!searchWrapRef.current?.contains(event.target)) {
        setSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const navItems = [
    { href: '/PlaceCanvas', label: 'Free Draw', icon: <Blocks />, isActive: currentSection === 'PlaceCanvas' },
    { href: resolvedArtworkHref, label: 'Artwork', icon: <Blocks />, isActive: currentSection === 'Canvas' },
    { href: '/Events', label: 'Events', icon: <Crown />, disabled: true },
    { href: '/Inventory/Artwork', label: 'Inventory', icon: <Package />, isActive: currentSection === 'Inventory' },
    { href: '/About', label: 'About', icon: <MessageCircleQuestion />, isActive: currentSection === 'About' },
  ];

  const filteredCanvasOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return canvasOptions.slice(0, 8);
    }

    return canvasOptions
      .filter((entry) => {
        const label = String(entry.label || '').toLowerCase();
        return label.startsWith(query) || label.includes(query);
      })
      .slice(0, 8);
  }, [canvasOptions, searchQuery]);

  const playerProgress = useMemo(
    () => getPlayerProgress(userProfile),
    [userProfile]
  );
  const levelAccent = useMemo(
    () => getLevelAccent(playerProgress?.level ?? 1),
    [playerProgress?.level]
  );
  const avatarRingRadius = 18;
  const avatarRingCircumference = 2 * Math.PI * avatarRingRadius;
  const avatarRingOffset = avatarRingCircumference * (1 - (playerProgress?.progressRatio ?? 0));

  const handleSearchSelect = (entry) => {
    setSearchQuery(entry.label);
    setSearchFocused(false);
    navigate(entry.href);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const normalizedQuery = searchQuery.trim();
    if (!normalizedQuery || containsBlockedSearchTerm(normalizedQuery)) {
      setSearchFocused(false);
      return;
    }

    const exactMatch = canvasOptions.find((entry) => entry.label.toLowerCase() === normalizedQuery.toLowerCase());
    if (exactMatch) {
      handleSearchSelect(exactMatch);
      return;
    }

    if (filteredCanvasOptions.length === 1) {
      handleSearchSelect(filteredCanvasOptions[0]);
      return;
    }

    navigate(`${searchBasePath}/${encodeURIComponent(normalizedQuery.toUpperCase())}`);
    setSearchFocused(false);
  };

  return (
    <Navbar
      isBordered
      classNames={{
        base: 'fixed top-0 z-40 w-full bg-slate-950 text-white border-indigo-300 h-[70px]',
        wrapper: 'w-full px-4 max-w-none flex justify-between items-center gap-4',
        brand: 'text-center',
        content: 'flex items-center',
        item: [
          'h-full flex items-center',
          "data-[active=true]:after:content-['']",
          'data-[active=true]:after:absolute data-[active=true]:after:bottom-0',
          'data-[active=true]:after:left-0 data-[active=true]:after:right-0',
          'data-[active=true]:after:h-[2px] data-[active=true]:after:rounded-[2px]',
          'data-[active=true]:after:bg-primary',
        ],
      }}
    >
      <NavbarContent className="gap-6" justify="start">
        {navItems.map((item) => (
          <NavbarItem key={item.label} isActive={item.isActive}>
            {item.disabled ? (
              <Tooltip content="Coming soon" color="secondary" placement="bottom" delay={500}>
                <div
                  aria-disabled="true"
                  tabIndex={-1}
                  className="relative flex items-center gap-2 text-slate-500 select-none pr-5"
                >
                  {item.icon}
                  <span>{item.label}</span>
                  <span className="pointer-events-none absolute top-2 right-2 translate-x-1/4 -translate-y-1/4 rotate-[20deg] origin-top-right bg-indigo-500 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full shadow-sm">
                    Soon
                  </span>
                </div>
              </Tooltip>
            ) : (
              <Link
                className={`flex items-center gap-2 transition-colors duration-200 ${
                  item.isActive ? 'text-white' : 'text-slate-400 hover:text-white'
                }`}
                href={item.href}
              >
                {item.icon}
                {item.label}
              </Link>
            )}
          </NavbarItem>
        ))}
      </NavbarContent>

      <NavbarContent className="flex-1 items-center justify-center gap-4" justify="center">
        <NavbarBrand className="flex-none">
          <img
            src={artParcelLogo}
            alt="ArtParcel Logo"
            className="h-36 w-auto object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
        </NavbarBrand>

        <form onSubmit={handleSearchSubmit} className="relative hidden min-[980px]:block w-[min(34vw,360px)] ml-24" ref={searchWrapRef}>
          <Input
            value={searchQuery}
            onValueChange={setSearchQuery}
            onFocus={() => setSearchFocused(true)}
            placeholder={searchPlaceholder}
            startContent={<Search className="h-4 w-4 text-slate-400" />}
            classNames={{
              inputWrapper: 'bg-slate-900/90 border border-slate-700 hover:border-indigo-400 data-[focus=true]:border-indigo-400 shadow-lg',
              input: 'text-white',
            }}
          />

          {searchFocused && filteredCanvasOptions.length > 0 && (
            <div className="absolute left-0 right-0 mt-2 rounded-2xl border border-slate-700 bg-slate-900/95 shadow-2xl overflow-hidden backdrop-blur">
              {filteredCanvasOptions.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSearchSelect(entry);
                  }}
                  className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-800/80 transition-colors"
                >
                  <div>
                    <div className="text-sm font-semibold text-white">{entry.label}</div>
                    <div className="text-xs text-slate-400">{entry.subtitle}</div>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-indigo-300">Open</div>
                </button>
              ))}
            </div>
          )}
        </form>

        {currentSection === 'Inventory' ? (
          <div className="absolute top-full mt-0.5 bg-slate-950 text-white text-sm px-4 py-1 rounded-b-xl shadow-lg border border-t-0 border-indigo-400 z-50">
            <Select
              aria-label="Category"
              selectedKeys={[currentCategory]}
              disableSelectorIconRotation
              classNames={{
                base: 'w-[160px] text-white',
                value: 'text-center',
                item: 'text-white',
              }}
              size="sm"
              variant="underlined"
              onChange={(e) => {
                const selected = e.target.value;
                if (selected !== currentCategory) {
                  navigate(`/Inventory/${selected}`);
                }
              }}
            >
              {['Artwork', 'Palettes'].map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </Select>
          </div>
        ) : null}
      </NavbarContent>

      <NavbarContent className="gap-8" justify="end">
        {isUserLoading ? (
          <NavbarItem>
            <Skeleton className="w-[100px] h-[32px] rounded-lg" />
          </NavbarItem>
        ) : userProfile ? (
          <>
            <NavbarItem className="cursor-pointer relative">
              <NavbarNewsItem />
            </NavbarItem>
            <NavbarItem>
              <div className="flex items-center gap-2 text-white px-4 py-2 rounded-lg bg-purple-900 hover:bg-purple-800 cursor-pointer transition">
                <ChromaIcon size={32} />
                <h1 className="font-semibold">{formatInt(userProfile.tokens || 0)}</h1>
              </div>
            </NavbarItem>
            <NavbarItem>
              <Button onPress={() => navigate('/Shop')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <Store />
                Get Pixel Drops
              </Button>
            </NavbarItem>
            <NavbarItem>
              <Dropdown
                placement="bottom-end"
                classNames={{
                  base: 'border-slate-700',
                  content: 'bg-slate-900 text-white border border-slate-700 rounded-2xl shadow-xl shadow-slate-900/50 p-2',
                  wrapper: 'bg-slate-900',
                }}
              >
                <DropdownTrigger>
                  <div className="flex items-center cursor-pointer">
                    <div className={`relative flex h-12 w-12 items-center justify-center ${levelAccent.pulse ? 'animate-pulse' : ''}`}>
                      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 44 44" aria-hidden="true">
                        <circle
                          cx="22"
                          cy="22"
                          r={avatarRingRadius}
                          fill="none"
                          stroke="rgba(71, 85, 105, 0.7)"
                          strokeWidth="3"
                        />
                        {levelAccent.ringSecondary ? (
                          <circle
                            cx="22"
                            cy="22"
                            r={avatarRingRadius}
                            fill="none"
                            stroke={levelAccent.ringSecondary}
                            strokeWidth="3"
                            strokeOpacity="0.4"
                            strokeLinecap="round"
                            strokeDasharray={avatarRingCircumference}
                            strokeDashoffset={Math.max(0, avatarRingOffset - avatarRingCircumference * 0.12)}
                          />
                        ) : null}
                        <circle
                          cx="22"
                          cy="22"
                          r={avatarRingRadius}
                          fill="none"
                          stroke={levelAccent.ring}
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeDasharray={avatarRingCircumference}
                          strokeDashoffset={avatarRingOffset}
                        />
                      </svg>
                      <Avatar
                        key={userProfile?.avatarImageData ? 'has-src' : 'no-src'}
                        name={userProfile?.username?.substring(0, 2)}
                        src={userProfile?.avatarImageData ? `data:image/png;base64,${userProfile.avatarImageData}` : undefined}
                        style={{ imageRendering: 'pixelated' }}
                        className="h-9 w-9 border border-slate-600"
                      />
                    </div>
                    <div className="ml-2 flex flex-col leading-tight">
                      <h4 className="font-semibold">{userProfile?.username}</h4>
                      <span className={`text-[11px] uppercase tracking-[0.18em] ${levelAccent.label}`}>
                        Lv {playerProgress.level}
                      </span>
                    </div>
                  </div>
                </DropdownTrigger>

                <DropdownMenu
                  aria-label="User Menu"
                  classNames={{
                    base: 'bg-transparent',
                  }}
                  itemClasses={{
                    base: [
                      'data-[hover=true]:bg-slate-800/60',
                      'data-[hover=true]:text-white',
                      'rounded-xl',
                    ],
                  }}
                >
                  <DropdownItem
                    key="profile-card"
                    className="p-0"
                    onClick={() => navigate(`/User/${userProfile.username}`, { state: { userSub: userProfile.sub } })}
                  >
                    <div className="w-[380px] rounded-xl bg-slate-800/60 border border-slate-700 p-4">
                      <div className="flex gap-3">
                        <div
                          className={`relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full ${levelAccent.pulse ? 'animate-pulse' : ''}`}
                          style={{ boxShadow: `0 0 24px ${levelAccent.glow}` }}
                        >
                          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 92 92" aria-hidden="true">
                            <circle
                              cx="46"
                              cy="46"
                              r="38"
                              fill="none"
                              stroke="rgba(71, 85, 105, 0.7)"
                              strokeWidth="7"
                            />
                            {levelAccent.ringSecondary ? (
                              <circle
                                cx="46"
                                cy="46"
                                r="38"
                                fill="none"
                                stroke={levelAccent.ringSecondary}
                                strokeWidth="7"
                                strokeOpacity="0.42"
                                strokeLinecap="round"
                                strokeDasharray={2 * Math.PI * 38}
                                strokeDashoffset={Math.max(0, (2 * Math.PI * 38) * (1 - (playerProgress?.progressRatio ?? 0)) - (2 * Math.PI * 38) * 0.12)}
                              />
                            ) : null}
                            <circle
                              cx="46"
                              cy="46"
                              r="38"
                              fill="none"
                              stroke={levelAccent.ring}
                              strokeWidth="7"
                              strokeLinecap="round"
                              strokeDasharray={2 * Math.PI * 38}
                              strokeDashoffset={(2 * Math.PI * 38) * (1 - (playerProgress?.progressRatio ?? 0))}
                            />
                          </svg>
                          <Avatar
                            key={userProfile?.avatarImageData ? 'has-src' : 'no-src'}
                            name={userProfile?.username?.substring(0, 2)}
                            src={userProfile?.avatarImageData ? `data:image/png;base64,${userProfile.avatarImageData}` : undefined}
                            style={{ imageRendering: 'pixelated' }}
                            className="h-20 w-20 text-5xl border border-slate-600"
                            size="lg"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-semibold truncat text-xl">{userProfile?.username}</div>
                            <div className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${levelAccent.badge}`}>
                              Lv {playerProgress.level}
                            </div>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-slate-900 border border-slate-700">
                              <RefreshCw size={18}/>
                              Refresh Time: <span className="text-indigo-300 font-semibold">
                                {Math.max(0, Number(userProfile?.extendTime ?? 0))}h
                              </span>
                            </span>

                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-slate-900 border border-slate-700">
                              <SquarePlus size={18}/>
                              Max Buy Size: <span className="text-indigo-300 font-semibold">
                                {Number(userProfile?.maxBuySize ?? 50)}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DropdownItem>

                  <DropdownItem
                    key="inventory"
                    startContent={<Package />}
                    onClick={() => navigate(`/Inventory`)}
                  >
                    Inventory
                  </DropdownItem>

                  <DropdownItem
                    key="support"
                    startContent={<Headset />}
                    onClick={() => navigate(`/Support`)}
                  >
                    Support
                  </DropdownItem>

                  {isAdmin && (
                    <DropdownItem
                      key="admin"
                      startContent={<Headset />}
                      onClick={() => setAdminModalOpen(true)}
                    >
                      Admin
                    </DropdownItem>
                  )}

                  <DropdownItem
                    key="signout"
                    color="danger"
                    startContent={<LogOut />}
                    onClick={async () => {
                      try {
                        await signOut();
                        setTimeout(() => navigate('/'), 200);
                      } catch (err) {
                        console.error('Error signing out:', err);
                      }
                    }}
                  >
                    Sign Out
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavbarItem>
            <AdminModal
              isOpen={adminModalOpen}
              onClose={() => setAdminModalOpen(false)}
              message="You must be logged in to send messages."
            />
          </>
        ) : (
          <NavbarItem>
            <Link className="text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg transition" href="/Auth">
              Log In
            </Link>
          </NavbarItem>
        )}
      </NavbarContent>
    </Navbar>
  );
}

